// ================================================================
// AUTH CONTEXT - Project Phoenix Optimized
// Removed blocking streak validation for instant app startup
// ================================================================

import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { clearLocalStorageStatistics } from '../utils/statsCleanup';
import { hasSessionCookie } from '../utils/cookieUtils';
import { logError } from '../utils/logger';
import apiService from '../services/api';

const AuthContext = createContext();

// Generate a unique tab ID for this browser tab
const TAB_ID = Math.random().toString(36).substring(2, 15);

// Export the context for test utilities
export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const syncTimeoutRef = useRef(null);

  // Check if user is logged in on app load
  useEffect(() => {
    // Only check auth if there are indicators the user might be logged in
    // This prevents unnecessary 401 requests when users are definitely not authenticated
    if (hasSessionCookie()) {
      fetchUser();
    } else {
      // No session cookie found, user is not authenticated
      setUser(null);
      setLoading(false);
    }
  }, []);

  // Proactive token refresh timer - refresh tokens before they expire
  useEffect(() => {
    if (!user) return; // Only run when user is authenticated

    // Refresh tokens every 14 minutes (before the 15-minute expiry)
    const refreshInterval = setInterval(async () => {
      try {
        await apiService.refreshToken();
      } catch (error) {
        // If refresh fails, user will be logged out on next API call
      }
    }, 14 * 60 * 1000); // 14 minutes

    return () => clearInterval(refreshInterval);
  }, [user]); // Re-run when user changes (login/logout)

  // Cross-tab synchronization - listen for auth changes in other tabs
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'auth-sync-event' && event.newValue) {
        try {
          const syncData = JSON.parse(event.newValue);
          
          // Ignore events from this tab or stale events (older than 5 seconds)
          if (syncData.tabId === TAB_ID || (Date.now() - syncData.timestamp) > 5000) {
            return;
          }
          
          console.log('Auth sync event detected from another tab:', syncData.type);
          
          // Clear any existing timeout to prevent rapid sync events
          if (syncTimeoutRef.current) {
            clearTimeout(syncTimeoutRef.current);
          }
          
          // Debounce the sync operation
          syncTimeoutRef.current = setTimeout(async () => {
            try {
              // Instead of reloading, just refresh the auth state
              if (syncData.type === 'login') {
                await fetchUser(true);
              } else if (syncData.type === 'logout') {
                setUser(null);
              }
            } catch (error) {
              logError('Error syncing auth state:', error);
            }
          }, 100); // Small delay to debounce
          
        } catch (error) {
          logError('Error parsing auth sync event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);


  const fetchUser = async (isSyncCall = false) => {
    try {
      const response = await apiService.get('/auth/me');

      if (response.ok) {
        const userData = await response.json();
        
        if (!isSyncCall && !user && userData) {
          clearLocalStorageStatistics();
          // Trigger cross-tab synchronization for other tabs
          localStorage.setItem('auth-sync-event', JSON.stringify({
            type: 'login',
            timestamp: Date.now(),
            tabId: TAB_ID
          }));
          // Clean up after a short delay to allow other tabs to read it
          setTimeout(() => {
            localStorage.removeItem('auth-sync-event');
          }, 1000);
        }
        
        setUser(userData);
        
        // PROJECT PHOENIX: No more streak validation needed!
        // Current streaks are calculated on-demand by components that need them.
        // This makes app startup instantaneous.
      } else {
        // No valid cookie, user not authenticated (expected when logged out)
        setUser(null);
      }
    } catch (error) {
      // Don't log 401 errors for auth endpoints as they're expected when not authenticated
      if (error.status === 401 && error.endpoint?.includes('/auth/me')) {
        // Silently handle expected 401 on auth check
        setUser(null);
      } else {
        logError('Error fetching user', error);
        setUser(null);
      }
    } finally {
      // This will now run immediately, making the app responsive.
      setLoading(false);
    }
  };

  const login = () => {
    window.location.href = apiService.getFullUrl('/auth/google');
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear httpOnly cookie
      await apiService.post('/auth/logout');
    } catch (error) {
      logError('Logout error', error);
    } finally {
      setUser(null);
      // Trigger cross-tab synchronization for other tabs
      localStorage.setItem('auth-sync-event', JSON.stringify({
        type: 'logout',
        timestamp: Date.now(),
        tabId: TAB_ID
      }));
      // Clean up after a short delay to allow other tabs to read it
      setTimeout(() => {
        localStorage.removeItem('auth-sync-event');
      }, 1000);
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
  };

  const refreshUser = async () => {
    try {
      // Only refresh if there are indicators the user might be logged in
      if (hasSessionCookie()) {
        await fetchUser();
      } else {
        // No session cookie, user is not authenticated
        setUser(null);
      }
    } catch (error) {
      logError('Error refreshing user', error);
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser,
    refreshUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};